import { buildDefaultConfig, clone, EMPTY_PREFS, migrateConfig } from '../../server/default-config.js';
import { canAccessAdmin as userCanAccessAdmin, hasPermission } from '../../server/permissions.js';

const CONFIG_EVENT = 'muyuan-config-change';
const USER_EVENT = 'muyuan-user-change';
const CONFIG_CACHE_KEY = 'muyuan_config_cache';
const USER_KEY = 'muyuan_user';
const TOKEN_KEY = 'muyuan_auth_token';
const LOCAL_PREFS_KEY = 'muyuan_local_prefs';
const ACCOUNT_PREFS_PREFIX = 'muyuan_account_prefs:';
const ENGINE_KEY = 'muyuan.engine';
const EDIT_PINS_KEY = 'muyuan.editPins';

function readConfigCache() {
  // Cached config gives the workstation a stable first paint while /api/config is still loading.
  try {
    const raw = localStorage.getItem(CONFIG_CACHE_KEY);
    if (!raw) return null;
    const value = JSON.parse(raw);
    if (!value || typeof value !== 'object' || !Array.isArray(value.groups)
      || value.groups.some((group) => !group || typeof group.name !== 'string' || !Array.isArray(group.items) || group.items.some((item) => !item || typeof item.name !== 'string' || typeof item.url !== 'string'))
      || (value.announcements && (!Array.isArray(value.announcements) || value.announcements.some((item) => !item || typeof item.text !== 'string')))
      || (value.todos && (!Array.isArray(value.todos) || value.todos.some((item) => !item || typeof item.text !== 'string')))) throw new Error('Invalid configuration cache');
    return migrateConfig(value);
  } catch {
    localStorage.removeItem(CONFIG_CACHE_KEY);
    return null;
  }
}

function persistConfigCache(config) {
  try {
    localStorage.setItem(CONFIG_CACHE_KEY, JSON.stringify(config));
  } catch {
    // The live server config is still the source of truth; cache failures only affect first paint.
  }
}

const cachedConfig = readConfigCache();
let configCache = cachedConfig || buildDefaultConfig();
let configReady = Boolean(cachedConfig);
let configPromise = null;
let configSynced = false;
let configError = null;
let configEpoch = 0;
let sessionGeneration = 0;
let loginAttempt = 0;
const preferenceQueues = new Map();
const preferenceVersions = new Map();

function sessionSnapshot() {
  return { token: getToken(), user: getUser(), generation: sessionGeneration };
}

function sameSession(snapshot) {
  return snapshot.generation === sessionGeneration && snapshot.token === getToken();
}

function sessionChangedError() {
  const error = new Error('登录状态已变化，请重新操作');
  error.code = 'session_changed';
  return error;
}

function assertSession(snapshot) {
  if (!sameSession(snapshot)) throw sessionChangedError();
}

window.addEventListener('storage', (event) => {
  if (event.key === TOKEN_KEY || event.key === null) sessionGeneration += 1;
});

async function fetchJsonWithTimeout(url, options) {
  const controller = new AbortController();
  let timeout;
  const deadline = new Promise((_, reject) => {
    timeout = setTimeout(() => {
      reject(new Error('请求超时，请检查网络后重试'));
      controller.abort();
    }, 15000);
  });
  try {
    const request = Promise.resolve(fetch(url, { ...options, signal: controller.signal })).then(async (response) => ({
      response,
      // Include body consumption in the deadline: a server may send headers
      // promptly and then stall while streaming the JSON body.
      body: response.ok ? await response.json() : await response.json().catch(() => ({})),
    }));
    return await Promise.race([request, deadline]);
  } catch (error) {
    if (error.name === 'AbortError') throw new Error('请求超时，请检查网络后重试');
    throw error;
  } finally {
    clearTimeout(timeout);
  }
}

export function configErrorMessage(error) {
  if (error?.status === 409) return '配置已被其他人更新，当前修改尚未保存。请先保留需要的内容，再重新载入最新配置后编辑。';
  if (error?.status === 401) return '登录已失效，当前修改尚未保存，请重新登录后重试。';
  if (error?.status === 403) return '当前账号没有保存权限，修改尚未保存。';
  return error?.message || '操作失败，请稍后重试';
}

async function requestJson(url, options = {}) {
  const session = options.headers?.Authorization ? sessionSnapshot() : null;
  const { response, body } = await fetchJsonWithTimeout(url, {
    ...options,
    headers: { 'Content-Type': 'application/json', ...(options.headers || {}) },
  });
  if (!response.ok) {
    const error = new Error(body.error || `Request failed: ${response.status}`);
    error.status = response.status;
    if (response.status === 401 && session && sameSession(session)) clearSession();
    throw error;
  }
  if (session) assertSession(session);
  return body;
}

function requestUpload(url, file) {
  return requestJson(url, {
    method: 'POST',
    headers: { ...authHeaders(), 'Content-Type': file?.type || 'application/octet-stream' },
    body: file,
  });
}

function emit(name) {
  window.dispatchEvent(new CustomEvent(name));
}

function authHeaders() {
  const token = getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function setSession(token, user) {
  sessionGeneration += 1;
  // Store login in localStorage by design: users should not need to log in again on the same browser.
  localStorage.setItem(TOKEN_KEY, token);
  sessionStorage.removeItem(TOKEN_KEY);
  localStorage.setItem(USER_KEY, JSON.stringify(user));
  emit(USER_EVENT);
}

function clearSession() {
  sessionGeneration += 1;
  loginAttempt += 1;
  localStorage.removeItem(TOKEN_KEY);
  sessionStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
  emit(USER_EVENT);
}

function normalizePrefs(prefs) {
  const ids = (value) => Array.isArray(value) ? [...new Set(value.filter((id) => typeof id === 'string'))] : [];
  return {
    catOrder: ids(prefs?.catOrder),
    itemOrder: Object.fromEntries(Object.entries(prefs?.itemOrder && typeof prefs.itemOrder === 'object' && !Array.isArray(prefs.itemOrder) ? prefs.itemOrder : {}).map(([key, value]) => [key, ids(value)])),
    pins: Array.isArray(prefs?.pins) ? ids(prefs.pins) : null,
  };
}

function loadLocalPrefs() {
  try {
    return normalizePrefs(JSON.parse(localStorage.getItem(LOCAL_PREFS_KEY) || 'null'));
  } catch {
    localStorage.removeItem(LOCAL_PREFS_KEY);
    return clone(EMPTY_PREFS);
  }
}

function saveLocalPrefs(prefs) {
  const next = normalizePrefs(prefs);
  localStorage.setItem(LOCAL_PREFS_KEY, JSON.stringify(next));
  return clone(next);
}

function accountPrefsKey(user = getUser()) {
  const username = String(user?.username || '').trim();
  return username ? ACCOUNT_PREFS_PREFIX + encodeURIComponent(username) : '';
}

function loadAccountPrefs(user = getUser()) {
  const key = accountPrefsKey(user);
  if (!key) return null;
  try {
    const raw = localStorage.getItem(key);
    return raw ? normalizePrefs(JSON.parse(raw)) : null;
  } catch {
    localStorage.removeItem(key);
    return null;
  }
}

function saveAccountPrefs(prefs, user = getUser()) {
  const key = accountPrefsKey(user);
  const next = normalizePrefs(prefs);
  if (key) localStorage.setItem(key, JSON.stringify(next));
  return clone(next);
}

function clearAccountPrefs(user = getUser()) {
  const key = accountPrefsKey(user);
  if (key) localStorage.removeItem(key);
}

function commitConfig(config) {
  configCache = migrateConfig(config);
  configReady = true;
  configSynced = true;
  configError = null;
  configEpoch += 1;
  persistConfigCache(configCache);
  emit(CONFIG_EVENT);
  return clone(configCache);
}

export function getConfigState() {
  return { ready: configReady, synced: configSynced, loading: Boolean(configPromise), error: configError };
}

export async function fetchConfig() {
  if (!configPromise) {
    const epoch = configEpoch;
    configError = null;
    configPromise = requestJson('/api/config')
      .then((config) => {
        // A GET started before a successful write must not undo that write.
        if (epoch === configEpoch) commitConfig(config);
        return clone(configCache);
      })
      .catch((error) => { configError = error; throw error; })
      .finally(() => { configPromise = null; emit(CONFIG_EVENT); });
    emit(CONFIG_EVENT);
  }
  return clone(await configPromise);
}

export async function fetchWeather(location) {
  return requestJson(`/api/weather?location=${encodeURIComponent(location || '')}`);
}

export function loadConfig() {
  return clone(configCache);
}

export function isConfigReady() {
  return configReady;
}

export async function saveConfig(config) {
  const saved = await requestJson('/api/config', {
    method: 'PUT',
    headers: authHeaders(),
    body: JSON.stringify(config),
  });
  return commitConfig(saved);
}

export async function resetConfig(revision) {
  const saved = await requestJson('/api/config/reset', {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ revision }),
  });
  return commitConfig(saved);
}

export async function uploadLogo(file) {
  return requestUpload('/api/admin/logo', file);
}

export async function savePromotionText(text, revision) {
  const promotion = await requestJson('/api/promotion', {
    method: 'PATCH',
    headers: authHeaders(),
    body: JSON.stringify({ text, revision }),
  });
  const { revision: _revision, ...value } = promotion;
  // A promotion-only response is not a complete snapshot of the new revision.
  // Keep the old revision until a fresh GET supplies all fields together.
  configCache = migrateConfig({ ...configCache, promotion: value });
  configEpoch += 1;
  configSynced = false;
  persistConfigCache(configCache);
  emit(CONFIG_EVENT);
  if (configPromise) await configPromise.catch(() => {});
  await fetchConfig().catch(() => {});
  return clone(value);
}

export function subscribeConfig(callback) {
  const handler = () => callback(clone(configCache), getConfigState());
  window.addEventListener(CONFIG_EVENT, handler);
  handler();
  fetchConfig().catch(() => {});
  return () => window.removeEventListener(CONFIG_EVENT, handler);
}

export async function login(username, password) {
  const attempt = ++loginAttempt;
  const session = sessionSnapshot();
  const result = await requestJson('/api/auth/login', {
    method: 'POST',
    body: JSON.stringify({ username, password }),
  });
  if (attempt !== loginAttempt || !sameSession(session)) throw sessionChangedError();
  setSession(result.token, result.user);
  return result.user;
}

export function isAuthed() {
  return Boolean(getToken() && getUser());
}

export function isAdmin() {
  const user = getUser();
  return Boolean(getToken() && user?.role === 'admin');
}

export function canAccessAdmin(user = getUser()) {
  return Boolean(getToken() && userCanAccessAdmin(user));
}

export function currentUserHasPermission(permission, user = getUser()) {
  return Boolean(getToken() && hasPermission(user, permission));
}

export function getToken() {
  const localToken = localStorage.getItem(TOKEN_KEY);
  if (localToken) return localToken;
  const sessionToken = sessionStorage.getItem(TOKEN_KEY);
  if (!sessionToken) return '';
  // Migrate old session-only logins created before the "remember forever" requirement.
  localStorage.setItem(TOKEN_KEY, sessionToken);
  sessionStorage.removeItem(TOKEN_KEY);
  return sessionToken;
}

export function logout() {
  clearSession();
}

export function getUser() {
  if (!getToken()) {
    localStorage.removeItem(USER_KEY);
    return null;
  }
  try {
    return JSON.parse(localStorage.getItem(USER_KEY) || 'null');
  } catch {
    return null;
  }
}

export function subscribeUser(callback) {
  const handler = () => callback(getUser());
  const storageHandler = (event) => { if ([TOKEN_KEY, USER_KEY, null].includes(event.key)) handler(); };
  window.addEventListener(USER_EVENT, handler);
  window.addEventListener('storage', storageHandler);
  return () => {
    window.removeEventListener(USER_EVENT, handler);
    window.removeEventListener('storage', storageHandler);
  };
}

export async function verifySession() {
  if (!getToken() || !getUser()) return null;
  const session = sessionSnapshot();
  try {
    const result = await requestJson('/api/auth/me', { headers: authHeaders() });
    assertSession(session);
    localStorage.setItem(USER_KEY, JSON.stringify(result.user));
    emit(USER_EVENT);
    return result.user;
  } catch (error) {
    if (error.status === 401 || error.code === 'session_changed') return null;
    throw error;
  }
}

export async function changePassword(currentPassword, newPassword) {
  const session = sessionSnapshot();
  const result = await requestJson('/api/auth/password', {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ currentPassword, newPassword }),
  });
  assertSession(session);
  setSession(result.token, result.user);
  return result.user;
}

function preferenceSessionKey(session) {
  return `${session.generation}:${session.token}`;
}

function enqueuePreferenceWrite(session, operation) {
  const key = preferenceSessionKey(session);
  const previous = preferenceQueues.get(key) || Promise.resolve();
  const next = previous.catch(() => {}).then(() => { assertSession(session); return operation(); });
  preferenceQueues.set(key, next);
  next.finally(() => { if (preferenceQueues.get(key) === next) preferenceQueues.delete(key); }).catch(() => {});
  return next;
}

export async function loadPrefsForCurrentUser() {
  const session = sessionSnapshot();
  if (!session.token) return loadLocalPrefs();
  const key = preferenceSessionKey(session);
  const version = preferenceVersions.get(key) || 0;
  const prefs = normalizePrefs(await requestJson('/api/preferences/me', { headers: { Authorization: `Bearer ${session.token}` } }));
  assertSession(session);
  // A slow initial GET cannot replace edits made after the request began.
  if ((preferenceVersions.get(key) || 0) !== version) return loadAccountPrefs(session.user) || prefs;
  saveAccountPrefs(prefs, session.user);
  return clone(prefs);
}

export async function savePrefsForCurrentUser(prefs) {
  const session = sessionSnapshot();
  if (!session.token) return saveLocalPrefs(prefs);
  const key = preferenceSessionKey(session);
  const version = (preferenceVersions.get(key) || 0) + 1;
  preferenceVersions.set(key, version);
  const optimistic = saveAccountPrefs(prefs, session.user);
  return enqueuePreferenceWrite(session, async () => {
    const saved = normalizePrefs(await requestJson('/api/preferences/me', {
      method: 'PUT',
      headers: { Authorization: `Bearer ${session.token}` },
      body: JSON.stringify(optimistic),
    }));
    assertSession(session);
    if (preferenceVersions.get(key) === version) saveAccountPrefs(saved, session.user);
    return clone(saved);
  });
}

export async function clearPrefsForCurrentUser() {
  const session = sessionSnapshot();
  if (!session.token) {
    localStorage.removeItem(LOCAL_PREFS_KEY);
    return clone(EMPTY_PREFS);
  }
  const key = preferenceSessionKey(session);
  preferenceVersions.set(key, (preferenceVersions.get(key) || 0) + 1);
  return enqueuePreferenceWrite(session, async () => {
    const saved = await requestJson('/api/preferences/me', { method: 'DELETE', headers: { Authorization: `Bearer ${session.token}` } });
    assertSession(session);
    clearAccountPrefs(session.user);
    return saved;
  });
}

export function loadCachedPrefsForCurrentUser(user = getUser()) {
  if (!getToken()) return loadLocalPrefs();
  return loadAccountPrefs(user) || clone(EMPTY_PREFS);
}

export async function listUsers() {
  return requestJson('/api/admin/users', { headers: authHeaders() });
}

export async function createUser(user) {
  return requestJson('/api/admin/users', {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(user),
  });
}

export async function updateUser(username, changes) {
  return requestJson(`/api/admin/users/${encodeURIComponent(username)}`, {
    method: 'PATCH',
    headers: authHeaders(),
    body: JSON.stringify(changes),
  });
}

export async function deleteUser(username) {
  return requestJson(`/api/admin/users/${encodeURIComponent(username)}`, {
    method: 'DELETE',
    headers: authHeaders(),
  });
}

export async function listAudit(limit = 80) {
  return requestJson(`/api/admin/audit?limit=${encodeURIComponent(limit)}`, { headers: authHeaders() });
}

export function getEngineId() {
  return localStorage.getItem(ENGINE_KEY) || 'baidu';
}

export function setEngineId(id) {
  localStorage.setItem(ENGINE_KEY, id);
}

export { clone, EMPTY_PREFS, EDIT_PINS_KEY };

export const MuyuanStore = {
  EMPTY_PREFS,
  loadConfig,
  isConfigReady,
  getConfigState,
  fetchConfig,
  fetchWeather,
  saveConfig,
  resetConfig,
  uploadLogo,
  savePromotionText,
  subscribeConfig,
  isAuthed,
  isAdmin,
  canAccessAdmin,
  hasPermission: currentUserHasPermission,
  login,
  verifySession,
  changePassword,
  logout,
  getUser,
  subscribeUser,
  loadCachedPrefsFor: loadCachedPrefsForCurrentUser,
  loadPrefsFor: loadPrefsForCurrentUser,
  savePrefsFor: savePrefsForCurrentUser,
  clearPrefsFor: clearPrefsForCurrentUser,
  listUsers,
  createUser,
  updateUser,
  deleteUser,
  listAudit,
};
